/**
 * Desktop app (Tauri) only: one line in Settings when the operating system
 * blocks WeaveIn's notifications (TRUS-668). Notifications are on by default
 * and the permission is asked once after sign-in; macOS and Windows never ask
 * again after a "no", so this says where to turn them back on.
 */
import { useEffect, useState } from 'react';
import { BellOff } from 'lucide-react';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { desktopNotifications } from '@/services/desktopNotifications';

const isTauri = (): boolean => typeof window !== 'undefined' && '__TAURI__' in window;

function isWindows(): boolean {
  try {
    return /Windows/i.test(navigator.userAgent || '');
  } catch {
    return false;
  }
}

export function DesktopNotificationBlockedHint() {
  const { t } = useAppTranslation();
  const [denied, setDenied] = useState(
    () => isTauri() && desktopNotifications.getPermissionState() === 'denied',
  );

  // The permission is known only after the post-sign-in check: look again.
  useEffect(() => {
    if (!isTauri()) return;
    const id = setTimeout(() => setDenied(desktopNotifications.getPermissionState() === 'denied'), 500);
    return () => clearTimeout(id);
  }, []);

  if (!denied) return null;

  return (
    <p
      className="flex items-start gap-2 rounded-lg bg-attention-soft px-3 py-2 text-sm text-attention-ink"
      data-testid="desktop-notifications-blocked"
      role="note"
    >
      <BellOff className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
      <span>
        {isWindows()
          ? t(
              'settings.desktopNotifyBlockedWindows',
              'Notifications are turned off for WeaveIn on this computer. To turn them on, open Settings > System > Notifications and switch on WeaveIn.',
            )
          : t(
              'settings.desktopNotifyBlockedMac',
              'Notifications are turned off for WeaveIn on this Mac. To turn them on, open System Settings > Notifications > WeaveIn and switch on Allow notifications.',
            )}
      </span>
    </p>
  );
}

export default DesktopNotificationBlockedHint;
